// Layer 3 (Transformations): Module 8's person-by-document compliance roster.
// Pure derivation only: no React, engine mutations or side effects.

import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import {
  BUNKER_HILL_CREWS,
  BUNKER_HILL_PERSONS,
  BUNKER_HILL_SEATS,
} from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  allDocuments,
  TRAINING_AS_OF,
  TRAINING_RECORDS,
  TRAINING_ROSTER,
  type DocumentRecord,
  type TrainingRecord,
  type TrainingRosterMember,
} from './seed';

export type TrainingComplianceRow = {
  personId: string;
  name: string;
  seatId: TrainingRosterMember['seatId'];
  seatLabel: string;
  roleId: TrainingRosterMember['roleId'];
  role: string;
  crewId?: TrainingRosterMember['crewId'];
  /** Paul labels crew panels with their letter; day roles have no crew. */
  panel: string;
  requiredCount: number;
  currentSignoffs: number;
  currentTrainingIds: string[];
  compliancePercent: number;
  overdueItems: number;
  criticalSopComplete: boolean;
  criticalSopOutstanding: Array<{
    documentId: string;
    currentVersion: string;
    state: 'stale' | 'missing';
    signedVersion?: string;
  }>;
};

export type TrainingComplianceInput = {
  roster?: readonly TrainingRosterMember[];
  records?: readonly TrainingRecord[];
  documents?: readonly DocumentRecord[];
  asOf?: string;
};

const personById = new Map(BUNKER_HILL_PERSONS.map((person) => [person.id, person] as const));
const seatById = new Map(BUNKER_HILL_SEATS.map((seat) => [seat.id, seat] as const));
const crewById = new Map(BUNKER_HILL_CREWS.map((crew) => [crew.id, crew] as const));

function isCurrent(record: TrainingRecord | undefined, document: DocumentRecord): boolean {
  return record?.signedVersion === document.version;
}

function panelFor(member: TrainingRosterMember): string {
  if (!member.crewId) return 'Day';
  const crew = crewById.get(member.crewId);
  return crew ? crew.id.slice('crew-'.length).toUpperCase() : '';
}

export function trainingComplianceRows(
  input: TrainingComplianceInput = {},
): TrainingComplianceRow[] {
  const roster = input.roster ?? TRAINING_ROSTER;
  const records = input.records ?? TRAINING_RECORDS;
  const documents = input.documents ?? allDocuments();
  const asOf = input.asOf ?? TRAINING_AS_OF;

  return roster.map((member) => {
    const person = personById.get(member.personId);
    const seat = seatById.get(member.seatId);
    const required = documents.filter((document) => document.requiredRoleIds.includes(member.roleId));
    const memberRecords = records.filter((record) => record.personId === member.personId);
    const currentTrainingIds = required
      .filter((document) => isCurrent(memberRecords.find((record) => record.documentId === document.id), document))
      .map((document) => document.id);
    const critical = required.filter((document) => document.criticalSop);
    const currentCriticalCount = critical.filter((document) => currentTrainingIds.includes(document.id)).length;
    const criticalSopOutstanding = critical.flatMap((document) => {
      const record = memberRecords.find((candidate) => candidate.documentId === document.id);
      if (isCurrent(record, document)) return [];
      return [{
        documentId: document.id,
        currentVersion: document.version,
        state: record?.signedVersion ? 'stale' as const : 'missing' as const,
        ...(record?.signedVersion ? { signedVersion: record.signedVersion } : {}),
      }];
    });
    const overdueItems = required.filter((document) => {
      const record = memberRecords.find((candidate) => candidate.documentId === document.id);
      return Boolean(record && record.dueAt < asOf && !isCurrent(record, document));
    }).length;
    const currentSignoffs = currentTrainingIds.length;

    return {
      personId: member.personId,
      name: person?.name ?? member.personId,
      seatId: member.seatId,
      seatLabel: seat?.label ?? member.seatId,
      roleId: member.roleId,
      role: member.roleLabel,
      crewId: member.crewId,
      panel: panelFor(member),
      requiredCount: required.length,
      currentSignoffs,
      currentTrainingIds,
      compliancePercent: required.length === 0 ? 0 : Math.round((currentSignoffs / required.length) * 100),
      overdueItems,
      criticalSopComplete: critical.length > 0 && currentCriticalCount === critical.length,
      criticalSopOutstanding,
    };
  });
}

const MONTHS: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

/** `D MMM YYYY` and `YYYY-MM-DD` to UTC-day integers for cursor comparisons. */
function epochDay(value: string): number | undefined {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const display = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec(value);
  if (!display) return undefined;
  const month = MONTHS[display[2]!];
  return month === undefined
    ? undefined
    : Date.UTC(Number(display[3]), month, Number(display[1]));
}

function oneMonthBefore(asOf: string): number {
  const date = new Date(`${asOf}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() - 1);
  return date.getTime();
}

function recentDocumentCount(documents: readonly DocumentRecord[], asOf: string): number {
  return recentDocuments(documents, asOf).length;
}

export type TrainingComplianceKpi = {
  id: string;
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  tone: VizTone;
};

export function trainingComplianceKpis(
  input: TrainingComplianceInput = {},
): TrainingComplianceKpi[] {
  const rows = trainingComplianceRows(input);
  const totalRequired = rows.reduce((sum, row) => sum + row.requiredCount, 0);
  const totalCurrent = rows.reduce((sum, row) => sum + row.currentSignoffs, 0);
  const compliance = totalRequired === 0 ? 0 : Math.round((totalCurrent / totalRequired) * 100);
  const criticalPeople = rows.filter((row) => row.criticalSopComplete).length;
  const overdue = rows.reduce((sum, row) => sum + row.overdueItems, 0);
  const updated = recentDocumentCount(allDocuments(), input.asOf ?? TRAINING_AS_OF);

  const criticalGroups = new Map<string, {
    names: string[];
    panel: string;
    documentId: string;
    currentVersion: string;
    state: 'stale' | 'missing';
  }>();
  rows.forEach((row) => {
    row.criticalSopOutstanding.forEach((item) => {
      const key = [row.panel, item.documentId, item.currentVersion, item.state].join('|');
      const group = criticalGroups.get(key) ?? {
        names: [],
        panel: row.panel,
        documentId: item.documentId,
        currentVersion: item.currentVersion,
        state: item.state,
      };
      group.names.push(row.name);
      criticalGroups.set(key, group);
    });
  });
  const criticalSub = [...criticalGroups.values()].map((group) =>
    `${group.names.join(' · ')} — ${group.panel === 'Day' ? 'Day' : `Panel ${group.panel}`} — ${group.documentId} ${group.currentVersion}${group.state === 'missing' ? ' · missing' : ''}`,
  ).join('; ') || 'All current';

  const overdueByPanel = rows.reduce((counts, row) => {
    if (row.panel !== 'Day' && row.overdueItems > 0) {
      counts.set(row.panel, (counts.get(row.panel) ?? 0) + row.overdueItems);
    }
    return counts;
  }, new Map<string, number>());
  const concentratedPanel = [...overdueByPanel.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];

  return [
    {
      id: 'overall-compliance',
      label: 'Overall training compliance',
      value: `${compliance}%`,
      sub: 'Target ≥90%',
      tone: compliance >= 90 ? 'ok' : 'warn',
    },
    {
      id: 'critical-sops',
      label: 'Critical SOPs compliant',
      value: `${criticalPeople} of ${rows.length}`,
      sub: criticalSub,
      tone: criticalPeople === rows.length ? 'ok' : 'bad',
    },
    {
      id: 'overdue-items',
      label: 'Total overdue items',
      value: String(overdue),
      ...(concentratedPanel ? { sub: `Panel ${concentratedPanel[0]} · ${concentratedPanel[1]} overdue` } : {}),
      tone: overdue > 0 ? 'warn' : 'ok',
    },
    {
      id: 'new-content',
      label: 'New content since last month',
      value: String(updated),
      unit: 'documents',
      tone: 'info',
    },
  ];
}

// ---------------------------------------------------------------------------
// The band (locked comp `documents.html` B): Paul's four KPIs, each figure set
// against its comparison. The KPI projection above stays the source of every
// figure; the band only adds the comparison each figure is read against.
// ---------------------------------------------------------------------------

/** A comparison line: `strong` segments carry the lead weight; `nowrap` holds a token (a document ref or the target) on one line. */
export type ComparisonSegment = { text: string; strong?: boolean; nowrap?: boolean };

export type TrainingBandCell = {
  id: TrainingComplianceKpi['id'];
  label: string;
  figure: string;
  unit?: string;
  comparison: ComparisonSegment[];
  /** The top rule's state; `none` draws no rule (a count is not a state). */
  tone: 'ok' | 'warn' | 'bad' | 'none';
};

/** The documents published in the month before the cursor, in library order. */
export function recentDocuments(
  documents: readonly DocumentRecord[] = allDocuments(),
  asOf: string = TRAINING_AS_OF,
): DocumentRecord[] {
  const cursor = epochDay(asOf);
  if (cursor === undefined) return [];
  const cutoff = oneMonthBefore(asOf);
  return documents.filter((document) => {
    const updated = epochDay(document.updatedAt);
    return updated !== undefined && updated >= cutoff && updated <= cursor;
  });
}

export function trainingComplianceBand(input: TrainingComplianceInput = {}): TrainingBandCell[] {
  const rows = trainingComplianceRows(input);
  const kpis = trainingComplianceKpis(input);
  const documents = input.documents ?? allDocuments();
  const required = rows.reduce((sum, row) => sum + row.requiredCount, 0);
  const current = rows.reduce((sum, row) => sum + row.currentSignoffs, 0);
  const open = required - current;
  const kpi = (id: string) => kpis.find((item) => item.id === id)!;
  const overall = kpi('overall-compliance');
  const critical = kpi('critical-sops');
  const overdue = kpi('overdue-items');
  const panelOverdue = rows.reduce((counts, row) => {
    if (row.panel !== 'Day' && row.overdueItems > 0) counts.set(row.panel, (counts.get(row.panel) ?? 0) + row.overdueItems);
    return counts;
  }, new Map<string, number>());
  const topPanel = [...panelOverdue.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  const recent = recentDocuments(documents, input.asOf ?? TRAINING_AS_OF);
  const bandTone = (tone: VizTone): TrainingBandCell['tone'] =>
    tone === 'ok' || tone === 'warn' || tone === 'bad' ? tone : 'none';

  // The critical comparison rebuilt from `critical.sub` with the document ref
  // (`PR-01 r3`) held on one line: the ref token breaks after `PR-` if the cell
  // wraps mid-token, and the joined text is unchanged.
  const criticalComparison: ComparisonSegment[] = (critical.sub ?? '').split('; ').flatMap((part, index) => {
    const lead = index > 0 ? [{ text: '; ' }] : [];
    const bits = part.split(' — ');
    if (bits.length < 3) return [...lead, { text: part }];
    return [
      ...lead,
      { text: `${bits.slice(0, -1).join(' — ')} — ` },
      { text: bits[bits.length - 1]!, nowrap: true },
    ];
  });

  return [
    {
      id: overall.id,
      label: overall.label,
      figure: overall.value.replace(/%$/, ''),
      unit: '%',
      comparison: [
        { text: `${current} of ${required}`, strong: true },
        { text: ' sign-offs current · ' },
        { text: 'target ≥ 90%', nowrap: true },
      ],
      tone: bandTone(overall.tone),
    },
    {
      id: critical.id,
      label: critical.label,
      figure: critical.value.split(' of ')[0]!,
      unit: `of ${rows.length}`,
      comparison: criticalComparison,
      tone: bandTone(critical.tone),
    },
    {
      id: overdue.id,
      label: overdue.label,
      figure: overdue.value,
      comparison: [
        { text: 'of ' },
        { text: String(open), strong: true },
        { text: ' open sign-offs' },
        ...(topPanel
          ? [{ text: ' · ' }, { text: String(topPanel[1]), strong: true }, { text: ` on Panel ${topPanel[0]}` }]
          : []),
      ],
      tone: bandTone(overdue.tone),
    },
    {
      id: 'new-content',
      label: kpi('new-content').label,
      figure: String(recent.length),
      unit: `of ${documents.length}`,
      comparison: [{ text: recent.map((document) => `${document.id} ${document.version}`).join(' · ') }],
      tone: 'none',
    },
  ];
}

// ---------------------------------------------------------------------------
// Sign-off matrix (locked comp `documents.html` B): nine people down, nine
// documents across; each cell current, signed an older revision, or unsigned
// with its due date. A training register is a person × document grid, and the
// gap is a shape on it.
// ---------------------------------------------------------------------------

export type SignoffCellState = 'current' | 'stale' | 'overdue' | 'due' | 'none';

export type SignoffCell = {
  documentId: string;
  state: SignoffCellState;
  /** The superseded version a stale sign-off holds (`r2`). */
  signedVersion?: string;
  /** ISO due date of an open sign-off. */
  dueAt?: string;
  /** `07 Jun` — the due date as the matrix prints it. */
  dueLabel?: string;
};

export type SignoffColumnGroup = 'Procedures' | 'Round sheets' | 'Training';

export type SignoffColumn = {
  id: string;
  title: string;
  kind: DocumentRecord['kind'];
  group: SignoffColumnGroup;
  version: string;
  /** The version a pending revision publishes as (`v4`), when one is in review. */
  pendingVersion?: string;
  criticalSop: boolean;
  /** The first column of its group: it draws the group's left hairline. */
  groupStart: boolean;
  current: number;
  required: number;
};

/** The per-row compliance tone, on the band's thresholds (warn ≥ 75, ok ≥ 90). */
export function complianceTone(percent: number): 'ok' | 'warn' | 'bad' {
  return percent >= 90 ? 'ok' : percent >= 75 ? 'warn' : 'bad';
}

export type SignoffMatrixRow = {
  personId: string;
  name: string;
  role: string;
  panel: string;
  cells: SignoffCell[];
  compliancePercent: number;
  complianceTone: 'ok' | 'warn' | 'bad';
  overdueItems: number;
  /** The row's open cells (stale, overdue, due) in column order — the narrow-width roster's refs. */
  open: SignoffCell[];
};

export type SignoffMatrix = {
  groups: Array<{ label: SignoffColumnGroup; span: number }>;
  columns: SignoffColumn[];
  rows: SignoffMatrixRow[];
  current: number;
  required: number;
  /** One real mark per state for the legend; a state absent from the register draws no key. */
  legend: { stale?: string; overdue?: string; due?: string; critical?: string };
};

const GROUP_BY_KIND: Record<DocumentRecord['kind'], SignoffColumnGroup> = {
  procedure: 'Procedures',
  'round-sheet': 'Round sheets',
  training: 'Training',
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-06-07` or `07 Jun 2026` → `07 Jun`. */
export function shortDayLabel(value: string): string {
  const day = epochDay(value);
  if (day === undefined) return value;
  const date = new Date(day);
  return `${String(date.getUTCDate()).padStart(2, '0')} ${SHORT_MONTHS[date.getUTCMonth()]}`;
}

/** The version a drafted revision publishes as: the published version's trailing number plus one. */
export function nextVersion(version: string): string {
  const match = /^(.*?)(\d+)$/.exec(version);
  return match ? `${match[1]}${Number(match[2]) + 1}` : version;
}

/** The version a document's pending revision will publish as, when one is in review. */
export function pendingVersionOf(document: DocumentRecord): string | undefined {
  return document.kind === 'training' && document.pendingRevision ? nextVersion(document.version) : undefined;
}

function cellFor(
  member: TrainingRosterMember,
  document: DocumentRecord,
  records: readonly TrainingRecord[],
  asOf: string,
): SignoffCell {
  if (!document.requiredRoleIds.includes(member.roleId)) return { documentId: document.id, state: 'none' };
  const record = records.find((row) => row.personId === member.personId && row.documentId === document.id);
  if (isCurrent(record, document)) return { documentId: document.id, state: 'current' };
  const due = record?.dueAt;
  const dated = due ? { dueAt: due, dueLabel: shortDayLabel(due) } : {};
  if (record?.signedVersion) return { documentId: document.id, state: 'stale', signedVersion: record.signedVersion, ...dated };
  return { documentId: document.id, state: due && due < asOf ? 'overdue' : 'due', ...dated };
}

export function signoffMatrix(input: TrainingComplianceInput = {}): SignoffMatrix {
  const roster = input.roster ?? TRAINING_ROSTER;
  const records = input.records ?? TRAINING_RECORDS;
  const documents = input.documents ?? allDocuments();
  const asOf = input.asOf ?? TRAINING_AS_OF;
  const complianceRows = trainingComplianceRows(input);

  const rows: SignoffMatrixRow[] = roster.map((member, index) => {
    const cells = documents.map((document) => cellFor(member, document, records, asOf));
    const row = complianceRows[index]!;
    return {
      personId: member.personId,
      name: row.name,
      role: row.role,
      panel: row.panel,
      cells,
      compliancePercent: row.compliancePercent,
      complianceTone: complianceTone(row.compliancePercent),
      overdueItems: row.overdueItems,
      open: cells.filter((cell) => cell.state === 'stale' || cell.state === 'overdue' || cell.state === 'due'),
    };
  });

  const columns: SignoffColumn[] = documents.map((document, index) => {
    const column = rows.map((row) => row.cells[index]!);
    return {
      id: document.id,
      title: document.title,
      kind: document.kind,
      group: GROUP_BY_KIND[document.kind],
      version: document.version,
      ...(pendingVersionOf(document) ? { pendingVersion: pendingVersionOf(document) } : {}),
      criticalSop: document.criticalSop,
      groupStart: index === 0 || GROUP_BY_KIND[documents[index - 1]!.kind] !== GROUP_BY_KIND[document.kind],
      current: column.filter((cell) => cell.state === 'current').length,
      required: column.filter((cell) => cell.state !== 'none').length,
    };
  });

  const groups: SignoffMatrix['groups'] = [];
  columns.forEach((column) => {
    const last = groups[groups.length - 1];
    if (last && last.label === column.group) last.span += 1;
    else groups.push({ label: column.group, span: 1 });
  });

  const cells = rows.flatMap((row) => row.cells);
  const legend: SignoffMatrix['legend'] = {};
  const stale = cells.find((cell) => cell.state === 'stale');
  const overdue = cells.find((cell) => cell.state === 'overdue');
  const due = cells.find((cell) => cell.state === 'due');
  const critical = columns.find((column) => column.criticalSop);
  if (stale?.signedVersion) legend.stale = stale.signedVersion;
  if (overdue?.dueLabel) legend.overdue = overdue.dueLabel;
  if (due?.dueLabel) legend.due = due.dueLabel;
  if (critical) legend.critical = critical.id;

  return {
    groups,
    columns,
    rows,
    legend,
    current: columns.reduce((sum, column) => sum + column.current, 0),
    required: columns.reduce((sum, column) => sum + column.required, 0),
  };
}

/** The register's open sign-offs — every cell the current signatures leave outstanding. */
export function openSignoffCount(matrix: SignoffMatrix): number {
  return matrix.required - matrix.current;
}

// ---------------------------------------------------------------------------
// Who signs what: every open sign-off grouped by why it is open — a newer
// revision published over a signature, or unsigned by a due date — each
// person named once per group with the documents they owe.
// ---------------------------------------------------------------------------

export type OpenSignoffRef = {
  documentId: string;
  /** `PR-01 r2` for a stale signature, the document id otherwise. */
  label: string;
  tone: 'bad' | 'neutral';
};

export type OpenSignoffPerson = {
  personId: string;
  name: string;
  panel: string;
  refs: OpenSignoffRef[];
};

export type OpenSignoffGroup = {
  id: string;
  label: string;
  tone: 'bad' | 'neutral';
  count: number;
  people: OpenSignoffPerson[];
};

const PANEL_ORDER = ['A', 'B', 'C', 'Day'];

function panelRank(panel: string): number {
  const index = PANEL_ORDER.indexOf(panel);
  return index < 0 ? PANEL_ORDER.length : index;
}

export function openSignoffGroups(input: TrainingComplianceInput = {}): OpenSignoffGroup[] {
  const matrix = signoffMatrix(input);
  const documents = input.documents ?? allDocuments();
  const asOf = input.asOf ?? TRAINING_AS_OF;
  const groups = new Map<string, OpenSignoffGroup & { sort: [number, string, number] }>();

  matrix.rows.forEach((row) => {
    row.open.forEach((cell) => {
      const columnIndex = matrix.columns.findIndex((column) => column.id === cell.documentId);
      const document = documents[columnIndex]!;
      const stale = cell.state === 'stale';
      const key = stale ? `stale:${document.id}` : `unsigned:${cell.dueAt ?? ''}`;
      const pastDue = Boolean(cell.dueAt && cell.dueAt < asOf);
      const group = groups.get(key) ?? {
        id: key,
        label: stale
          ? `Stale after ${document.id} ${document.version} · ${shortDayLabel(document.updatedAt)}`
          : `Unsigned · due ${cell.dueLabel ?? ''}`,
        tone: stale || pastDue ? 'bad' as const : 'neutral' as const,
        count: 0,
        people: [],
        sort: stale ? [0, '', columnIndex] as [number, string, number] : [1, cell.dueAt ?? '', 0] as [number, string, number],
      };
      group.count += 1;
      let person = group.people.find((candidate) => candidate.personId === row.personId);
      if (!person) {
        person = { personId: row.personId, name: row.name, panel: row.panel, refs: [] };
        group.people.push(person);
      }
      person.refs.push({
        documentId: cell.documentId,
        label: stale ? `${cell.documentId} ${cell.signedVersion}` : cell.documentId,
        tone: group.tone,
      });
      groups.set(key, group);
    });
  });

  const rosterIndex = new Map(matrix.rows.map((row, index) => [row.personId, index] as const));
  return [...groups.values()]
    .sort((a, b) => a.sort[0] - b.sort[0] || a.sort[1].localeCompare(b.sort[1]) || a.sort[2] - b.sort[2])
    .map(({ sort, ...group }) => {
      void sort;
      return {
        ...group,
        people: [...group.people].sort((a, b) =>
          b.refs.length - a.refs.length
          || panelRank(a.panel) - panelRank(b.panel)
          || (rosterIndex.get(a.personId) ?? 0) - (rosterIndex.get(b.personId) ?? 0)),
      };
    });
}

// ---------------------------------------------------------------------------
// What a pending revision does to the register when it publishes: every
// current signature on the published version goes stale (the logic doc's
// stale-on-publish rule), so those people re-sign.
// ---------------------------------------------------------------------------

export type PublishEffect = {
  documentId: string;
  version: string;
  nextVersion: string;
  resign: Array<{ personId: string; name: string; panel: string }>;
};

export function pendingPublishEffects(input: TrainingComplianceInput = {}): PublishEffect[] {
  const documents = input.documents ?? allDocuments();
  const before = trainingComplianceRows(input);
  return documents.flatMap((document) => {
    const next = pendingVersionOf(document);
    if (!next || document.kind !== 'training') return [];
    const published = documents.map((candidate) =>
      candidate.id === document.id ? { ...document, version: next, pendingRevision: undefined } : candidate);
    const after = trainingComplianceRows({ ...input, documents: published });
    const resign = before
      .filter((row, index) => row.currentTrainingIds.includes(document.id) && !after[index]!.currentTrainingIds.includes(document.id))
      .map((row) => ({ personId: row.personId, name: row.name, panel: row.panel }));
    return [{ documentId: document.id, version: document.version, nextVersion: next, resign }];
  });
}

/** Per-document sign-off state: the library tree's coverage line and the context panel's "Signed on" facts. */
export type DocumentSignoffs = {
  documentId: string;
  version: string;
  current: number;
  required: number;
  stale: Array<{ name: string; panel: string; signedVersion: string }>;
  /** Unsigned sign-offs grouped by due date, soonest first. */
  unsigned: Array<{ dueAt: string; dueLabel: string; pastDue: boolean; names: string[] }>;
};

export function documentSignoffs(documentId: string, input: TrainingComplianceInput = {}): DocumentSignoffs | undefined {
  const matrix = signoffMatrix(input);
  const index = matrix.columns.findIndex((column) => column.id === documentId);
  if (index < 0) return undefined;
  const column = matrix.columns[index]!;
  const stale: DocumentSignoffs['stale'] = [];
  const unsigned = new Map<string, DocumentSignoffs['unsigned'][number]>();
  matrix.rows.forEach((row) => {
    const cell = row.cells[index]!;
    if (cell.state === 'stale') {
      stale.push({ name: row.name, panel: row.panel, signedVersion: cell.signedVersion ?? '' });
    } else if (cell.state === 'overdue' || cell.state === 'due') {
      const key = cell.dueAt ?? '';
      const group = unsigned.get(key) ?? { dueAt: key, dueLabel: cell.dueLabel ?? '', pastDue: cell.state === 'overdue', names: [] };
      group.names.push(row.name);
      unsigned.set(key, group);
    }
  });
  return {
    documentId,
    version: column.version,
    current: column.current,
    required: column.required,
    stale,
    unsigned: [...unsigned.values()].sort((a, b) => a.dueAt.localeCompare(b.dueAt)),
  };
}
